"use client";

import {
  CopyButton,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  HINT,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { PROTEIN_MOLECULAR_WEIGHT_CALCULATOR_STRINGS } from "@/components/tools/protein-molecular-weight-calculator";
import { t } from "@/lib/i18n/interpolate";
import {
  type Analysis,
  type extinction,
  formulaText,
  netCharge,
  type PkaSet,
  PROTON_MASS,
  type parseProtein,
  RESIDUES,
} from "@/lib/tool-kit/biology-genetics/protein-properties";

type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

export type Strings = typeof PROTEIN_MOLECULAR_WEIGHT_CALCULATOR_STRINGS;

interface PanelProps {
  strings: Strings;
  fmt: Fmt;
  current: { name: string; parsed: ReturnType<typeof parseProtein> };
  proteins: readonly { name: string }[];
  rows: readonly (Analysis & {
    record: { name: string; parsed: { length: number } };
  })[];
  view: Analysis;
  selectedIndex: number;
  setPicked: (value: string) => void;
  picked: string;
  pkaId: PkaSet["id"];
  ph: number | null;
  summaryTsv: string;
}

export function ResultsPanel({
  strings,
  fmt,
  current,
  proteins,
  rows,
  view,
  selectedIndex,
  setPicked,
  pkaId,
  ph,
  summaryTsv,
}: PanelProps) {
  const phValid = ph !== null;
  return (
    <div aria-live="polite" className="space-y-6">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      {proteins.length > 1 ? (
        <SelectField
          id="pmw-record"
          label={strings.recordLabel}
          onChange={setPicked}
          options={proteins.map((record, index) => ({
            value: String(index),
            label: record.name,
          }))}
          value={String(selectedIndex)}
        />
      ) : null}
      {view.capped ? (
        <Notice>
          {t(strings.disulfidesCapped, { count: fmt(view.used) })}
        </Notice>
      ) : null}
      <StatGrid>
        <StatCard
          emphasis
          label={strings.averageMass}
          value={`${fmt(view.average, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${strings.unitDa}`}
        />
        <StatCard
          label={strings.monoMass}
          value={`${fmt(view.mono, { minimumFractionDigits: 4, maximumFractionDigits: 4 })} ${strings.unitDa}`}
        />
        <StatCard
          label={strings.length}
          value={`${fmt(current.parsed.length)} ${strings.unitAa}`}
        />
        <StatCard
          label={strings.pi}
          value={fmt(view.pi, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        />
        {phValid ? (
          <StatCard
            label={t(strings.chargeAt, {
              ph: fmt(ph ?? 0, { maximumFractionDigits: 2 }),
            })}
            value={fmt(netCharge(view.charge, ph ?? 0), {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          />
        ) : null}
        <StatCard
          label={strings.unitKda}
          value={fmt(view.average / 1000, { maximumFractionDigits: 3 })}
        />
      </StatGrid>
      <ul className="space-y-1 text-foreground text-sm">
        <li>
          {strings.formula}:{" "}
          <span className="font-mono">{formulaText(view.formula)}</span>
        </li>
        <li>
          {strings.protonated}:{" "}
          {fmt(view.mono + PROTON_MASS, {
            minimumFractionDigits: 4,
            maximumFractionDigits: 4,
          })}
        </li>
      </ul>
      {view.pi > 10 ? <p className={HINT}>{strings.basicWarning}</p> : null}
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.extinctionHeading}</h3>
        <p className={HINT}>{strings.extinctionNote}</p>
        <ExtinctionTable
          assumed={view.assumedPairs}
          average={view.average}
          epsilon={view.epsilon}
          fmt={fmt}
          strings={strings}
        />
      </div>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.compositionHeading}</h3>
        <CompositionTable
          composition={current.parsed.composition}
          fmt={fmt}
          length={current.parsed.length}
          strings={strings}
        />
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        text={t(strings.summaryLine, {
          name: current.name,
          length: current.parsed.length,
          average: view.average.toFixed(2),
          mono: view.mono.toFixed(4),
          pi: view.pi.toFixed(2),
          pka: pkaId,
          epsilon: view.epsilon.withCystines,
        })}
      />
      {proteins.length > 1 ? (
        <div className="space-y-2">
          <h3 className={WIDGET_H3}>{strings.summaryHeading}</h3>
          <SummaryTable fmt={fmt} rows={rows} strings={strings} />
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copySummary}
            text={summaryTsv}
          />
        </div>
      ) : null}
    </div>
  );
}

function ExtinctionTable({
  average,
  assumed,
  epsilon,
  strings,
  fmt,
}: {
  average: number;
  assumed: boolean;
  epsilon: ReturnType<typeof extinction>;
  strings: Strings;
  fmt: Fmt;
}) {
  const rows = [
    { label: strings.reducedRow, value: epsilon.reduced },
    {
      label: t(assumed ? strings.cystineAssumed : strings.cystineRow, {
        count: fmt(epsilon.cystines),
      }),
      value: epsilon.withCystines,
    },
  ];
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead>
          <tr>
            <th className={TH} scope="col">
              {strings.rowColumn}
            </th>
            <th className={`${TH} text-right`} scope="col">
              {strings.epsilonColumn}
            </th>
            <th className={`${TH} text-right`} scope="col">
              {strings.absorbanceColumn}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <td className={TD}>{row.label}</td>
              <td className={`${TD} text-right`}>{fmt(row.value)}</td>
              <td className={`${TD} text-right`}>
                {fmt(row.value / average, { maximumFractionDigits: 3 })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CompositionTable({
  composition,
  length,
  strings,
  fmt,
}: {
  composition: Record<string, number>;
  length: number;
  strings: Strings;
  fmt: Fmt;
}) {
  const present = RESIDUES.filter(
    (residue) => (composition[residue.one] ?? 0) > 0
  );
  return (
    <div className="max-h-96 overflow-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead className="sticky top-0 bg-card">
          <tr>
            <th className={TH} scope="col">
              {strings.residueColumn}
            </th>
            <th className={TH} scope="col">
              {strings.threeColumn}
            </th>
            <th className={`${TH} text-right`} scope="col">
              {strings.countColumn}
            </th>
            <th className={`${TH} text-right`} scope="col">
              {strings.percentColumn}
            </th>
          </tr>
        </thead>
        <tbody>
          {present.map((residue) => {
            const count = composition[residue.one] ?? 0;
            return (
              <tr key={residue.one}>
                <td className={`${TD} font-mono`}>{residue.one}</td>
                <td className={TD}>{residue.three}</td>
                <td className={`${TD} text-right`}>{fmt(count)}</td>
                <td className={`${TD} text-right`}>
                  {fmt((count / length) * 100, { maximumFractionDigits: 1 })}%
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SummaryTable({
  rows,
  strings,
  fmt,
}: {
  rows: readonly {
    record: { name: string; parsed: { length: number } };
    average: number;
    mono: number;
    pi: number;
    epsilon: ReturnType<typeof extinction>;
  }[];
  strings: Strings;
  fmt: Fmt;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead>
          <tr>
            <th className={TH} scope="col">
              {strings.nameColumn}
            </th>
            <th className={`${TH} text-right`} scope="col">
              {strings.lengthColumn}
            </th>
            <th className={`${TH} text-right`} scope="col">
              {strings.massColumn}
            </th>
            <th className={`${TH} text-right`} scope="col">
              pI
            </th>
            <th className={`${TH} text-right`} scope="col">
              ε₂₈₀
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: records may share a name
            <tr key={index}>
              <td className={TD}>{row.record.name}</td>
              <td className={`${TD} text-right`}>
                {fmt(row.record.parsed.length)}
              </td>
              <td className={`${TD} text-right`}>
                {fmt(row.average, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </td>
              <td className={`${TD} text-right`}>
                {fmt(row.pi, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </td>
              <td className={`${TD} text-right`}>
                {fmt(row.epsilon.withCystines)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
